import { FunctionComponent } from "react";
import { Trans, useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import {
	bandsOf,
	combineTiers,
	COMPANY_TIERS,
	ExampleCriterion,
	overallBandsOf,
	overallTierOf,
	PERSONA_TIERS,
	SCORE_EXAMPLE,
	ScoreTier,
	sideEdges,
	TierBand,
	tierOf,
	totalOf,
} from "./leadScoreCombination";

const TIER_STYLE: Record<ScoreTier, string> = {
	P1: "bg-status-success-light text-status-success",
	P2: "bg-status-warning-light text-tag-warning-dark",
	P3: "bg-tag-warning-light text-score-p3",
	DQ: "bg-status-error-light text-status-error",
};

const Chip: FunctionComponent<{ tier: ScoreTier }> = ({ tier }) => (
	<span
		className={classNames(
			"inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold",
			TIER_STYLE[tier]
		)}
	>
		{tier}
	</span>
);

interface TierBandsProps {
	bands: TierBand[];
	tier: ScoreTier;
	testId: string;
}

/**
 * Where the borders between the grades run: each grade as the inclusive point
 * range it covers, sized by how many points it spans, with the range the total
 * landed in filled in. The chip beside the total says *which* grade came out;
 * this says *why* — and what the next one up would have taken.
 */
const TierBands: FunctionComponent<TierBandsProps> = ({ bands, tier, testId }) => {
	const { t } = useTranslation();

	return (
		<div className="mt-2" data-testid={testId}>
			<div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-disabled">
				{t("leadScoring.how.example.bands")}
			</div>
			<div className="flex gap-1">
				{bands.map((band) => (
					<div
						key={band.tier}
						className={classNames(
							"flex items-baseline justify-center gap-1.5 whitespace-nowrap rounded px-2 py-1 text-[11px]",
							band.tier === tier ? TIER_STYLE[band.tier] : "bg-background-light text-text-meta"
						)}
						data-testid={`${testId}-${band.tier}`}
						style={{ flex: `${band.to - band.from + 1} 1 0%` }}
					>
						<span className="font-bold">{band.tier}</span>
						<span className="font-medium tabular-nums">
							{band.from}–{band.to}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

interface ExampleSideProps {
	title: string;
	criteria: ExampleCriterion[];
	tier: ScoreTier;
	bandsTestId: string;
}

/** One side of the worked example: its criteria, its total, and the grade that total lands in. */
const ExampleSide: FunctionComponent<ExampleSideProps> = ({
	title,
	criteria,
	tier,
	bandsTestId,
}) => {
	const { t } = useTranslation();
	const { points, max } = totalOf(criteria);

	return (
		<div className="rounded-lg border border-border-light bg-white p-3">
			<div className="mb-1.5 text-xs font-semibold text-text">{title}</div>
			<ul className="flex flex-col gap-1">
				{criteria.map((criterion) => (
					<li
						key={criterion.label}
						className="flex items-baseline justify-between gap-3 text-[11px]"
					>
						<span className="text-text-meta">
							{t(`leadScoring.how.example.${criterion.label}`)}
						</span>
						<span className="shrink-0 font-medium text-text">+{criterion.points}</span>
					</li>
				))}
			</ul>
			<div className="mt-2 flex items-center justify-between gap-3 border-t border-border-light pt-2 text-[11px]">
				<span className="text-text-meta">
					{t("leadScoring.how.example.total", { points, max })}
				</span>
				<Chip tier={tier} />
			</div>
			<TierBands bands={bandsOf(max)} testId={bandsTestId} tier={tier} />
		</div>
	);
};

/**
 * The worked example under the grid: two totals, each banded against its own
 * maximum, then added and banded again — the step the grid cannot draw.
 */
const ScoringExample: FunctionComponent = () => {
	const { t } = useTranslation();
	const company = totalOf(SCORE_EXAMPLE.company);
	const persona = totalOf(SCORE_EXAMPLE.persona);
	const companyTier = tierOf(company.points, company.max);
	const personaTier = tierOf(persona.points, persona.max);
	const overall = overallTierOf(company.points, persona.points, company.max, persona.max);
	// The overall borders are these two pairs added, which is what `bandsMath` spells out.
	const companyEdges = sideEdges(company.max);
	const personaEdges = sideEdges(persona.max);

	return (
		<div className="mt-4 rounded-lg bg-background-light p-3" data-testid="how-scoring-example">
			<div className="mb-2 text-xs font-semibold text-text">
				{t("leadScoring.how.example.title")}
			</div>
			<div className="grid grid-cols-1 gap-2 tb:grid-cols-3">
				<ExampleSide
					bandsTestId="how-example-company-bands"
					criteria={SCORE_EXAMPLE.company}
					tier={companyTier}
					title={t("leadScoring.how.example.company")}
				/>
				<ExampleSide
					bandsTestId="how-example-persona-bands"
					criteria={SCORE_EXAMPLE.persona}
					tier={personaTier}
					title={t("leadScoring.how.example.persona")}
				/>
				<div className="flex flex-col justify-center rounded-lg border border-border-light bg-white p-3">
					<div className="mb-1.5 text-xs font-semibold text-text">
						{t("leadScoring.how.example.overall")}
					</div>
					<div className="mb-2 text-[11px] text-text-meta">
						{t("leadScoring.how.example.sum", {
							company: company.points,
							persona: persona.points,
							points: company.points + persona.points,
							max: company.max + persona.max,
						})}
					</div>
					<div data-testid="how-example-overall">
						<Chip tier={overall} />
					</div>
					<TierBands
						bands={overallBandsOf(company.max, persona.max)}
						testId="how-example-overall-bands"
						tier={overall}
					/>
					<p className="mt-1.5 text-[10px] leading-relaxed text-text-meta">
						{t("leadScoring.how.example.bandsMath", {
							companyP3: companyEdges[0],
							personaP3: personaEdges[0],
							companyP2: companyEdges[1],
							personaP2: personaEdges[1],
						})}
					</p>
				</div>
			</div>
			<p className="mt-2 text-[11px] leading-relaxed text-text-meta">
				{t("leadScoring.how.example.note", {
					company: companyTier,
					persona: personaTier,
					overall,
				})}
			</p>
		</div>
	);
};

/**
 * How a lead's P1/P2/P3 comes out of the criteria, as the grid the mockup draws
 * (PREC-4079) — company score down the side, persona score across the top.
 *
 * The engine adds the two point totals rather than combining the two grades, so
 * only the cells where both sides agree have one answer; the others carry the
 * range they can produce. `combineTiers` documents why, and the worked example
 * below the grid shows one range cell being decided by the points.
 */
export const HowScoringWorks: FunctionComponent = () => {
	const { t } = useTranslation();

	const axis = "text-[10px] font-bold uppercase tracking-wider text-text-disabled";
	const head =
		"rounded-md bg-background-light px-2 py-1.5 text-center font-semibold text-text-meta";

	return (
		<div
			className="rounded-xl border border-border-light bg-white p-4"
			data-testid="how-scoring-works"
		>
			<h3 className="mb-2 text-sm font-semibold text-text">{t("leadScoring.how.title")}</h3>
			<p className="mb-3 max-w-[900px] text-xs leading-relaxed text-text-meta">
				<Trans
					components={{ b: <b className="font-semibold text-text" /> }}
					i18nKey="leadScoring.how.body"
				/>
			</p>
			<div className="max-w-[720px]">
				<div className="mb-1 grid grid-cols-[104px_1fr]">
					<span />
					<span className={axis}>{t("leadScoring.how.axisPersona")}</span>
				</div>
				<table
					className="w-full table-fixed border-separate border-spacing-1 text-xs"
					data-testid="how-scoring-grid"
				>
					<thead>
						<tr>
							<th
								className={classNames(axis, "w-[104px] pr-2 text-right align-bottom")}
								scope="col"
							>
								{t("leadScoring.how.axisCompany")}
							</th>
							{PERSONA_TIERS.map((persona) => (
								<th key={persona} className={head} scope="col">
									{persona}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{COMPANY_TIERS.map((company) => (
							<tr key={company}>
								<th className={classNames(head, "pr-2 text-right")} scope="row">
									{company}
								</th>
								{PERSONA_TIERS.map((persona) => {
									const outcome = combineTiers(company, persona);
									const certain = outcome.length === 1;

									return (
										<td
											key={persona}
											className={classNames(
												"rounded-md px-2 py-1.5 text-center font-semibold",
												certain ? TIER_STYLE[outcome[0]] : "bg-background-light text-text-disabled"
											)}
											data-testid={`how-cell-${company}-${persona}`}
										>
											{certain ? outcome[0] : `${outcome[0]}–${outcome[outcome.length - 1]}`}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<p className="mt-3 max-w-[900px] text-[11px] leading-relaxed text-text-meta">
				{t("leadScoring.how.rangeNote")}
			</p>
			<ScoringExample />
		</div>
	);
};
